import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  DollarSign, 
  Phone, 
  Eye, 
  Clock, 
  ShieldCheck, 
  TrendingUp, 
  ChefHat,
  Sparkles,
  CheckCircle,
  ArrowRight,
  Loader2
} from "lucide-react";
import { Button } from "../ui/button";

const SavingsCategory = ({ icon: Icon, label, value, description, delay, color = "gold" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4 }}
    className="glass-card rounded-lg p-4 border border-gold/20 hover:border-gold/40 transition-colors"
  >
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center shrink-0`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 mb-1">{label}</p>
        <p className={`text-xl font-inter font-bold text-${color}`}>{value}</p>
        <p className="text-xs text-slate-500 mt-1">{description}</p>
      </div>
    </div>
  </motion.div>
);

export const SavingsReport = ({ 
  reportData, 
  isLoading = false, 
  onStartTrial,
  trialLoading = false,
  restaurantName = "Your Restaurant"
}) => {
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (reportData) {
      const timer = setTimeout(() => setShowDetails(true), 800);
      return () => clearTimeout(timer);
    }
  }, [reportData]);

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0A1244] rounded-xl border border-gold/30 p-6 my-4"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
            <Loader2 className="w-4 h-4 text-gold animate-spin" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">Analyzing your data...</p>
            <p className="text-xs text-slate-400">Calculating potential savings across all Argus modules</p>
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-indigo-700/30 rounded-lg animate-pulse" />
          ))}
        </div>
      </motion.div>
    );
  }

  if (!reportData) return null;

  const {
    monthly_total = 0,
    annual_total = 0,
    compatibility_score = 0,
    breakdown = {},
    recommendations = [],
    restaurant_name = restaurantName
  } = reportData;

  const getScoreColor = (score) => {
    if (score >= 85) return "text-lime";
    if (score >= 70) return "text-gold";
    if (score >= 50) return "text-orange-400";
    return "text-red-400";
  };

  const getScoreBorder = (score) => {
    if (score >= 85) return "border-lime shadow-[0_0_20px_rgba(0,255,65,0.2)]";
    if (score >= 70) return "border-gold shadow-[0_0_20px_rgba(212,175,55,0.2)]";
    if (score >= 50) return "border-orange-400";
    return "border-red-400";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-[#0A1244] to-[#060D3A] rounded-xl border border-gold/30 overflow-hidden my-4"
    >
      {/* Header */}
      <div className="bg-gold/10 border-b border-gold/20 px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-gold" />
            </div>
            <div>
              <h3 className="font-inter text-sm font-bold text-gold">Argus Xenia Savings Report</h3>
              <p className="text-xs text-slate-400">Personalized for {restaurant_name}</p>
            </div>
          </div>
          <div className={`w-16 h-16 rounded-full border-3 ${getScoreBorder(compatibility_score)} flex items-center justify-center bg-[#040F49]`}>
            <span className={`text-xl font-inter font-black ${getScoreColor(compatibility_score)}`}>
              {compatibility_score}
            </span>
          </div>
        </div>
      </div>

      {/* Total Savings Summary */}
      <div className="px-5 py-4 border-b border-gold/10">
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center"
          >
            <p className="text-xs text-slate-400 mb-1">Monthly Savings</p>
            <p className="text-2xl font-inter font-black text-gold">
              ${monthly_total.toLocaleString()}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center"
          >
            <p className="text-xs text-slate-400 mb-1">Annual Savings</p>
            <p className="text-2xl font-inter font-black text-lime">
              ${annual_total.toLocaleString()}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Savings Breakdown */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="px-5 py-4 space-y-3"
          >
            <p className="text-xs font-medium text-slate-300 mb-3">Savings Breakdown by Module</p>
            
            {breakdown.missed_calls > 0 && (
              <SavingsCategory
                icon={Phone}
                label="Supervisor Echo (AI Receptionist)"
                value={`$${breakdown.missed_calls.toLocaleString()}/mo`}
                description="Recovered revenue from missed calls & after-hours bookings"
                delay={0.1}
                color="gold"
              />
            )}
            
            {breakdown.labor > 0 && (
              <SavingsCategory
                icon={Clock}
                label="Labor Optimization"
                value={`$${breakdown.labor.toLocaleString()}/mo`}
                description="Reduced overtime, efficient scheduling, automated tasks"
                delay={0.2}
                color="gold"
              />
            )}
            
            {breakdown.food_waste > 0 && (
              <SavingsCategory
                icon={Eye}
                label="Supervisor Apollo (Vision AI)"
                value={`$${breakdown.food_waste.toLocaleString()}/mo`}
                description="Reduced food waste through portion control & inventory alerts"
                delay={0.3}
                color="lime"
              />
            )}
            
            {breakdown.training > 0 && (
              <SavingsCategory
                icon={ChefHat}
                label="Supervisor Athena (SOP Builder)"
                value={`$${breakdown.training.toLocaleString()}/mo`}
                description="Faster onboarding, reduced training time, consistent execution"
                delay={0.4}
                color="gold"
              />
            )}
            
            {breakdown.safety > 0 && (
              <SavingsCategory
                icon={ShieldCheck}
                label="Safety & Compliance"
                value={`$${breakdown.safety.toLocaleString()}/mo`}
                description="Prevented incidents, avoided fines, reduced liability"
                delay={0.5}
                color="lime"
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recommendations */}
      {recommendations.length > 0 && showDetails && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="px-5 py-4 border-t border-gold/10"
        >
          <p className="text-xs font-medium text-slate-300 mb-3">Why You're a Great Fit</p>
          <div className="space-y-2">
            {recommendations.map((rec, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-lime shrink-0 mt-0.5" />
                <p className="text-xs text-slate-400">{rec}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* CTA */}
      <div className="px-5 py-4 bg-gold/5 border-t border-gold/20">
        <Button
          onClick={onStartTrial}
          disabled={trialLoading}
          className="w-full bg-gold hover:bg-gold/90 text-indigo-700 font-inter font-bold py-3 rounded-lg transition-all"
          data-testid="savings-report-trial-cta"
        >
          {trialLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Setting Up Your Trial...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Start Your Free Trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
        <p className="text-[10px] text-slate-500 text-center mt-2">
          No credit card required • Access to all currently available V1 capabilities
        </p>
      </div>
    </motion.div>
  );
};

export default SavingsReport;
